import { motion } from "framer-motion";
import { ArrowRight, MousePointer2 } from "lucide-react";
import SectionLabel from "@/components/sections/SectionLabel";

const scriptedFlow = ["Login", "Dashboard", "Logout"];

const exploratoryFlow = [
  "Login",
  "Resize browser",
  "Open another tab",
  "Go Incognito",
  "Duplicate tab",
  "Navigate backwards",
  "Refresh",
  "Expire token",
  "Upload invalid files",
  "Throttle network",
  "Switch devices",
  "Try unexpected combinations",
];

export default function Exploratory() {
  return (
    <section
      data-testid="exploratory-section"
      className="relative py-24 md:py-32 border-t border-border"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left text */}
          <div className="lg:col-span-5">
            <SectionLabel>Exploratory Testing</SectionLabel>
            <h2 className="font-display font-semibold mt-4 text-balance text-4xl sm:text-5xl md:text-[56px] leading-[1.02] tracking-[-0.025em]">
              It doesn&apos;t execute scripts.
              <br />
              <span className="text-[hsl(var(--accent))]">It explores.</span>
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-md">
              Scripted automation only finds bugs you already predicted. Liskov AI
              wanders the way real users do — unpredictably, persistently and across
              every plausible state of your product.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed max-w-md">
              Because that&apos;s what real users do.
            </p>
          </div>

          {/* Right visual */}
          <div className="lg:col-span-7" data-testid="exploratory-visual">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Scripted */}
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="premium-card p-6 relative"
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="mono-label text-muted-foreground">Scripted</span>
                  <span className="mono-label text-muted-foreground/70">linear</span>
                </div>
                <div className="space-y-2">
                  {scriptedFlow.map((step, i) => (
                    <div key={step} className="flex items-center gap-3">
                      <span className="font-mono text-[10px] text-muted-foreground w-5">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-sm text-foreground/70">{step}</span>
                      {i < scriptedFlow.length - 1 && (
                        <ArrowRight className="ml-auto h-3.5 w-3.5 text-muted-foreground/50" />
                      )}
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-xs text-muted-foreground leading-relaxed">
                  Predictable. Brittle. Misses what users actually do.
                </p>
              </motion.div>

              {/* Exploratory */}
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="premium-card p-6 relative overflow-hidden glow-ring"
                style={{
                  background:
                    "linear-gradient(180deg, hsl(var(--accent) / 0.06), hsl(var(--card)) 60%)",
                }}
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="mono-label text-[hsl(var(--accent))]">Liskov AI</span>
                  <span className="mono-label text-foreground/70 inline-flex items-center gap-1.5">
                    <MousePointer2 className="h-3 w-3" />
                    live
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {exploratoryFlow.map((step, i) => (
                    <motion.span
                      key={step}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.35, delay: 0.05 * i }}
                      className="px-2.5 py-1 rounded-full text-[12px] border border-[hsl(var(--accent)/0.35)] bg-[hsl(var(--accent)/0.12)] text-foreground/90"
                    >
                      {step}
                    </motion.span>
                  ))}
                </div>
                <p className="mt-6 text-xs text-muted-foreground leading-relaxed">
                  Branches at every step. Curious. Stateful. Catches the unknown.
                </p>

                {/* glowing dot */}
                <span className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-[hsl(var(--accent)/0.18)] blur-3xl" />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
